import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// import { useNavigate } from 'react-router-dom';

const PROFILE_CONFIG = {
  developer: { initial: 'D', gradient: 'from-[#30D158] to-[#0A8F3C]' },
  recruiter: { initial: 'R', gradient: 'from-apple-blue to-[#0055CC]' },
  athlete:   { initial: 'A', gradient: 'from-[#FF9F0A] to-[#D97706]' },
};

const Header = ({ onSearch, searchQuery, persona, onSwitchPersona, onLock }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const profile = PROFILE_CONFIG[persona] || PROFILE_CONFIG.developer;


  // Close search on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isMenuOpen) setIsMenuOpen(false);
        if (isSearchOpen) {
          setIsSearchOpen(false);
          onSearch('');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, isMenuOpen ,onSearch]);

  // Sync: if searchQuery is cleared externally, close the input
  // Close menu on outside click

  useEffect(() => {
    // if (searchQuery === '' && isSearchOpen) {
    //   // Keep open if user manually cleared — only close if truly reset

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
     return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]); // [searchQuery, isSearchOpen]);


  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed left-0 right-0 z-50 bg-pure-black/80 backdrop-blur-xl border-b border-white/10"
      style={{ top: '32px' }} 
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          onClick={() => window.location.href = '/'}
          //  onClick={() => navigate('/')}
          className="text-2xl font-bold text-pure-white tracking-tight cursor-pointer hover:text-apple-blue transition-colors"
          data-testid="logo"
        >
          NVSR
        </motion.div>

        {/* Right section */}
        <div className="flex items-center space-x-6">
          {/* Search */}
          <div className="relative">
            {isSearchOpen ? (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 'auto', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder="Search..."
                  className="bg-white/10 text-pure-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-apple-blue w-64"
                  autoFocus
                  data-testid="search-input"
                />
                <button
                  onClick={() => {
                    setIsSearchOpen(false);
                    onSearch('');
                  }}
                  className="ml-2 text-gray-400 hover:text-pure-white transition-colors"

                >
                  ✕
                </button>
              </motion.div>
            ) : (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsSearchOpen(true)}
                className="text-pure-white hover:text-apple-blue transition-colors"
                data-testid="search-icon"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </motion.button>
            )}
          </div>

          {/* Profile
          <motion.div
            whileHover={{ scale: 1.1 }}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-apple-blue to-purple-600 flex items-center justify-center cursor-pointer"
            data-testid="profile-icon"
          >
            <span className="text-pure-white font-semibold">N</span>
          </motion.div> */}
                    {/* Profile Icon + Dropdown */}
          <div className="relative" ref={menuRef}>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`w-10 h-10 rounded-full bg-gradient-to-br ${profile.gradient} flex items-center justify-center cursor-pointer`}
              data-testid="profile-icon"
            >
              <span className="text-pure-white font-semibold">{profile.initial}</span>
            </motion.div>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute right-0 mt-3 w-52 rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50"
                  style={{ background: '#1C1C1E' }}
                >
                  {/* Current persona label */}
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Viewing as</p>
                    <p className="text-sm text-pure-white font-medium mt-0.5 capitalize">{persona}</p>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        onSwitchPersona?.();
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-pure-white hover:bg-white/5 transition-colors flex items-center gap-3"
                    >
                      <span className="text-base">👤</span>
                      Switch User
                    </button>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        onLock?.();
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-pure-white hover:bg-white/5 transition-colors flex items-center gap-3"
                    >
                      <span className="text-base">🔒</span>
                      Lock Screen
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
